import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Panel } from "@/components/portal/Bits";
import { Instagram, ArrowUpRight } from "lucide-react";

function Avatar({ a }) {
  const initials = (a.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");
  if (a.headshot_url) {
    return <img src={a.headshot_url} alt={a.name} className="h-16 w-16 rounded-md object-cover object-top border border-border" />;
  }
  return (
    <div className="h-16 w-16 rounded-md bg-secondary border border-border grid place-items-center font-display text-lg text-white/70">
      {initials}
    </div>
  );
}

function Meta({ label, value }) {
  if (!value) return null;
  return (
    <span className="text-sm text-muted-foreground">{value}</span>
  );
}

function Card({ a }) {
  const metaLine = [a.classification, a.class_year].filter(Boolean).join(" · ");
  const physLine = [a.position, a.height, a.weight ? `${a.weight} lb` : null].filter(Boolean).join(" · ");
  const linkable = !!a.media_kit_token;

  const inner = (
    <Panel
      className={`p-5 h-full transition-colors ${linkable ? "hover:border-[#E1FF00]/40 cursor-pointer" : ""}`}
      data-testid={`public-athlete-card-${a.id}`}
    >
      <div className="flex items-start gap-4">
        <Avatar a={a} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-display text-xl text-white leading-tight">{a.name}</h3>
            {linkable && <ArrowUpRight className="h-4 w-4 text-[#E1FF00] shrink-0 mt-1" />}
          </div>
          <p className="text-sm text-white/80 mt-0.5">{a.school}</p>
          {metaLine && <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1.5">{metaLine}</p>}
          {a.commitment && (
            <p className="text-xs text-[#E1FF00] mt-1 font-medium" data-testid={`public-commit-${a.id}`}>Committed · {a.commitment}</p>
          )}
        </div>
      </div>

      {physLine && <p className="text-sm text-muted-foreground mt-4">{physLine}</p>}

      {(a.instagram_handle || a.x_handle) && (
        <div className="flex flex-wrap gap-3 mt-3">
          {a.instagram_handle && (
            <a href={a.instagram_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs text-white/90 hover:text-[#E1FF00] transition-colors"
              data-testid={`public-ig-${a.id}`}>
              <Instagram className="h-3.5 w-3.5" /> @{a.instagram_handle}
            </a>
          )}
          {a.x_handle && (
            <a href={a.x_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs text-white/90 hover:text-[#E1FF00] transition-colors"
              data-testid={`public-x-${a.id}`}>
              <span className="font-semibold">𝕏</span> @{a.x_handle}
            </a>
          )}
        </div>
      )}

      {linkable && (
        <p className="text-xs text-[#E1FF00] mt-4 font-medium">View media kit →</p>
      )}
    </Panel>
  );

  return linkable
    ? <Link to={`/kit/${a.media_kit_token}`} className="block h-full">{inner}</Link>
    : <div className="h-full">{inner}</div>;
}

export default function PublicRoster() {
  const [athletes, setAthletes] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Roster · Merch Run NIL";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/roster");
        setAthletes(data.athletes || []);
      } catch {
        setError(true);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background grain">
      <div className="mx-auto max-w-5xl px-5 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-10">
          <Logo size={40} />
          <span className="overline">Merch Run NIL</span>
        </div>

        <h1 className="font-display-tight text-4xl sm:text-5xl text-white" data-testid="public-roster-title">Roster</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Merch Run's NIL client roster. Tap any athlete with a shared media kit to view their public kit.
        </p>

        {error ? (
          <p className="text-muted-foreground mt-12" data-testid="public-roster-error">The roster is temporarily unavailable.</p>
        ) : athletes === null ? (
          <p className="text-muted-foreground mt-12">Loading…</p>
        ) : athletes.length === 0 ? (
          <p className="text-muted-foreground mt-12">No athletes listed yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="public-roster-grid">
            {athletes.map((a) => <Card key={a.id} a={a} />)}
          </div>
        )}

        <p className="text-xs text-muted-foreground/60 text-center mt-16">
          The Merch Dash · Merch Run NIL
        </p>
      </div>
    </div>
  );
}
